/** Recipes endpoints. */

import type { EffectiveAccess, SharedByView } from "./notes";
import { ApiClient } from "./client";

export type { EffectiveAccess, SharedByView };

/** Caller-relative scope for ``GET /recipes``. ``"mine"`` returns only
 *  recipes the caller owns, ``"shared"`` only recipes shared *with* the
 *  caller, ``"all"`` (default) returns both. */
export type RecipesScope = "mine" | "shared" | "all";

export interface IngredientPayload {
  name: string;
  quantity?: string;
  unit?: string;
}

export interface StepPayload {
  content: string;
}

export interface IngredientResponse {
  id: string;
  position: number;
  name: string;
  quantity: string;
  unit: string;
}

export interface StepResponse {
  id: string;
  position: number;
  content: string;
}

export interface RecipeResponse {
  id: string;
  user_id: string;
  title: string;
  description: string;
  servings: number;
  prep_minutes: number;
  cook_minutes: number;
  ingredients: IngredientResponse[];
  steps: StepResponse[];
  tag_ids: string[];
  color: string | null;
  created_at: string;
  updated_at: string;
  /** Owner of the recipe — same as ``user_id`` for backwards compat,
   *  surfaced explicitly so the UI doesn't conflate "the API caller"
   *  with "the owner". */
  owner_id: string;
  /** Non-null on recipes shared *with* the caller. Null for recipes
   *  the caller owns. */
  shared_by: SharedByView | null;
  /** Effective access the caller has on this row. */
  my_access: EffectiveAccess;
}

export interface RecipeListResponse {
  recipes: RecipeResponse[];
}

export interface CreateRecipeRequest {
  title: string;
  description?: string;
  servings?: number;
  prep_minutes?: number;
  cook_minutes?: number;
  ingredients?: IngredientPayload[];
  steps?: StepPayload[];
  tags?: readonly string[];
  color?: string | null;
}

export interface UpdateRecipeRequest {
  title?: string;
  description?: string;
  servings?: number;
  prep_minutes?: number;
  cook_minutes?: number;
  ingredients?: IngredientPayload[];
  steps?: StepPayload[];
  tags?: readonly string[];
  color?: string | null;
}

export const recipesApi = (client: ApiClient) => ({
  list(opts: { scope?: RecipesScope } = {}): Promise<RecipeListResponse> {
    const query: Record<string, string | undefined> = {};
    if (opts.scope !== undefined) query.scope = opts.scope;
    return client.request<RecipeListResponse>("/recipes", {
      method: "GET",
      query,
    });
  },
  get(id: string): Promise<RecipeResponse> {
    return client.request<RecipeResponse>(`/recipes/${id}`, { method: "GET" });
  },
  create(body: CreateRecipeRequest): Promise<RecipeResponse> {
    return client.request<RecipeResponse>("/recipes", { method: "POST", json: body });
  },
  update(id: string, body: UpdateRecipeRequest): Promise<RecipeResponse> {
    return client.request<RecipeResponse>(`/recipes/${id}`, {
      method: "PATCH",
      json: body,
    });
  },
  delete(id: string): Promise<void> {
    return client.request<void>(`/recipes/${id}`, { method: "DELETE" });
  },
});
